import { useState, useEffect } from 'react'
import { getUserShareLinks, deleteShareLink } from '../../services/api'
import { useDataStore } from '../../store/useDataStore'

interface ShareLink {
  shareId: string
  projectUuid: string
  createdAt: string
  expiresAt: string | null
  allowDownload?: boolean
  message?: string | null
  hasPassword?: boolean
  password?: string | null
  lockInterface?: boolean
  shareUrl: string
}

interface AllShareLinksDialogProps {
  onClose: () => void
}

// 从原生代码获取userUuid（与原生代码保持一致）
const getUserUuid = (): string | null => {
  const win = window as any
  // 优先使用原生的 ensureUserUuid 函数
  if (typeof win.ensureUserUuid === 'function') {
    return win.ensureUserUuid()
  }
  // 降级：直接从 window.data 获取
  if (win.data && win.data.userUuid) {
    return win.data.userUuid
  }
  return null
}

const AllShareLinksDialog = ({ onClose }: AllShareLinksDialogProps) => {
  const { data } = useDataStore()
  const [isLoading, setIsLoading] = useState(true)
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([])
  const [error, setError] = useState<string | null>(null)
  const [copiedShareId, setCopiedShareId] = useState<string | null>(null)
  const [visiblePasswordIds, setVisiblePasswordIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    // 加载分享链接
    loadAllShareLinks()
  }, [])

  // 根据projectUuid获取项目名称
  const getProjectName = (projectUuid: string): string => {
    const project = data.projects.find((p: any) => p.uuid === projectUuid)
    if (project) {
      return project.name || '未命名项目'
    }
    // 如果找不到，尝试从window.data获取（兼容原生代码）
    const win = window as any
    if (win.data && win.data.projects) {
      const nativeProject = win.data.projects.find((p: any) => p.uuid === projectUuid)
      if (nativeProject) {
        return nativeProject.name || '未命名项目'
      }
    }
    return '未命名项目'
  }

  const loadAllShareLinks = async () => {
    const userUuid = getUserUuid()
    if (!userUuid) {
      setError('用户未登录')
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      // 不传 projectUuid 参数，获取所有分享链接
      const response = await getUserShareLinks(userUuid)
      if (response.success) {
        setShareLinks(response.data || [])
      } else {
        setError(response.message || '加载分享链接失败')
      }
    } catch (err: any) {
      setError(err.message || '加载分享链接失败')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteShare = async (shareId: string) => {
    const userUuid = getUserUuid()
    if (!userUuid) return
    if (!confirm('确定要删除这个分享链接吗？')) return

    try {
      setError(null)
      const response = await deleteShareLink(userUuid, shareId)
      if (response.success) {
        // 显示toast（如果原生函数存在）
        const win = window as any
        if (typeof win.showToast === 'function') {
          win.showToast('分享链接已删除', 'success')
        }
        await loadAllShareLinks()
      } else {
        setError(response.message || '删除分享链接失败')
      }
    } catch (err: any) {
      setError(err.message || '删除分享链接失败')
    }
  }

  const handleCopyLink = (shareUrl: string) => {
    const shareId = shareUrl.replace('/share/', '')
    const fullUrl = `${window.location.origin}/share/${shareId}`

    // 设置复制成功状态
    setCopiedShareId(shareId)

    // 复制到剪贴板
    navigator.clipboard.writeText(fullUrl).then(() => {
      setTimeout(() => {
        setCopiedShareId(null)
      }, 2000)
    }).catch(() => {
      // 降级方案
      const textArea = document.createElement('textarea')
      textArea.value = fullUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)

      setTimeout(() => {
        setCopiedShareId(null)
      }, 2000)
    })
  }

  const handleOpenLink = (shareUrl: string) => {
    const shareId = shareUrl.replace('/share/', '')
    const fullUrl = `${window.location.origin}/share/${shareId}`
    window.open(fullUrl, '_blank')
  }

  // 点击遮罩层关闭
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[1999] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-lg shadow-xl min-w-[600px] max-w-[900px] w-[90%] max-h-[80vh] overflow-y-auto p-6 z-[2000]">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            所有分享链接
          </h3>
          <p className="text-sm text-gray-600">
            管理您的所有分享链接
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-10 text-gray-500">
            <div className="inline-block w-6 h-6 border-3 border-gray-200 border-t-blue-500 rounded-full animate-spin mb-3"></div>
            <p className="text-sm">加载中...</p>
          </div>
        ) : shareLinks.length === 0 ? (
          <div className="text-center py-10 text-gray-500 text-sm">
            暂无分享链接
          </div>
        ) : (
          <div className="space-y-4">
            {shareLinks.map((link) => {
              const shareId = link.shareUrl.replace('/share/', '')
              const fullUrl = `${window.location.origin}/share/${shareId}`
              const projectName = getProjectName(link.projectUuid)
              const isPasswordVisible = visiblePasswordIds.has(link.shareId)

              const togglePasswordVisibility = () => {
                setVisiblePasswordIds(prev => {
                  const newSet = new Set(prev)
                  if (newSet.has(link.shareId)) {
                    newSet.delete(link.shareId)
                  } else {
                    newSet.add(link.shareId)
                  }
                  return newSet
                })
              }

              return (
                <div
                  key={link.shareId}
                  className="p-4 bg-gray-50 border border-gray-200 rounded-md"
                >
                  <div className="mb-3">
                    <div className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                      <span>项目: {projectName}</span>
                      <span className="text-gray-400 text-xs font-mono">({link.projectUuid})</span>
                      {link.hasPassword && (
                        <button
                          onClick={togglePasswordVisibility}
                          className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 cursor-pointer"
                          title={isPasswordVisible ? '隐藏密码' : '显示密码'}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                          </svg>
                          {isPasswordVisible && link.password && (
                            <span className="text-xs font-mono bg-amber-100 px-1.5 py-0.5 rounded">
                              {link.password}
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={fullUrl}
                      readOnly
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded bg-white min-w-0"
                    />
                    <button
                      onClick={() => handleCopyLink(link.shareUrl)}
                      className={`px-4 py-2 text-sm text-white rounded transition-all duration-300 cursor-pointer flex-shrink-0 ${
                        copiedShareId === shareId
                          ? 'bg-green-600 scale-105 shadow-lg'
                          : 'bg-blue-600 hover:bg-blue-700'
                      }`}
                    >
                      {copiedShareId === shareId ? (
                        <span className="flex items-center gap-1">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          已复制
                        </span>
                      ) : (
                        '复制'
                      )}
                    </button>
                    <button
                      onClick={() => handleOpenLink(link.shareUrl)}
                      className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors cursor-pointer flex-shrink-0"
                    >
                      打开
                    </button>
                    <button
                      onClick={() => handleDeleteShare(link.shareId)}
                      className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors cursor-pointer flex-shrink-0"
                    >
                      删除
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>
                      创建时间: {new Date(link.createdAt).toLocaleString('zh-CN')}
                      {link.expiresAt ? ` | 过期时间: ${new Date(link.expiresAt).toLocaleString('zh-CN')}` : ' | 永久有效'}
                    </div>
                    <div>
                      允许下载: {link.allowDownload !== false ? '是' : '否'}
                      {link.message && ` | 提示信息: ${link.message}`}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div className="flex gap-2 justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  )
}

export default AllShareLinksDialog
